import React, { useState, useMemo, useEffect, useCallback } from "react";
import * as Styled from "./LibraryOutletsComponent.styled";
import { OutletCard } from "./outlet-card";
import { OutletForm, OutletFormData } from "./outlet-form";
import SearchIcon from "../../assets/images/icons/search.svg";
import { BaseButton, ILibraryOutlet, IProfile } from "../../libs";
import { blocksService } from "../../services";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { EmbedCodeModal } from "./embed-code-modal";

export const LibraryOutletsComponent: React.FC = () => {
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState<ILibraryOutlet[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState<ILibraryOutlet | null>(null);
  const [embedCodeOutlet, setEmbedCodeOutlet] = useState<ILibraryOutlet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<IProfile[]>([]);
  const [showEmbedCodeModal, setShowEmbedCodeModal] = useState<boolean>(false);

  // # TODO: Get the profile ids and use a dropdown in the form

  const fetchOutlets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await blocksService.getLibraryOutlets();
      setOutlets(data);
    } catch (err: any) {
      console.error("Failed to fetch outlets:", err);
      setError("Failed to load outlets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const data = await blocksService.getProfiles();
      console.log("Fetched profiles:", data);
      setProfiles(data);
    } catch (err: any) {
      toast.error("Failed to fetch profiles:", err);
    }
  }, []);

  useEffect(() => {
    fetchOutlets();
    fetchProfiles();
  }, [fetchProfiles]);

  // Filter outlets based on search query
  const filteredOutlets = useMemo(() => {
    return outlets.filter((outlet) =>
      outlet.outletName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [outlets, searchQuery]);

  const handleCreateOutlet = () => {
    setSelectedOutlet(null);
    setShowForm(true);
  };

  const handleEditOutlet = (outlet: ILibraryOutlet) => {
    setSelectedOutlet(outlet);
    setShowForm(true);
  };

  const handleToggleEnabled = async (outletId: string, enabled: boolean) => {
    // Note: This functionality may need a dedicated API endpoint
    // For now, we'll update it via the update endpoint
    try {
      const outlet = outlets.find(o => o.outletId === outletId);
      if (!outlet) return;

      // You may need to add an 'enabled' field to the API
      // For now, we'll just update the local state
      setOutlets((prev) =>
        prev.map((outlet) =>
          outlet.outletId === outletId ? { ...outlet, deleted: !enabled } : outlet
        )
      );
    } catch (err: any) {
      console.error("Failed to toggle outlet:", err);
      setError("Failed to update outlet status.");
    }
  };

  const handleSaveOutlet = async (data: OutletFormData) => {
    try {
      setLoading(true);
      setError(null);

      if (selectedOutlet) {
        // Edit existing outlet
        await blocksService.updateLibraryOutlet({
          outletId: selectedOutlet.outletId,
          outletName: data.outletName,
          outletUrl: data.outletUrl,
          linkedProfileId: data.linkedProfileId,
        });
      } else {
        // Create new outlet
        await blocksService.createLibraryOutlet({
          outletName: data.outletName,
          outletUrl: data.outletUrl,
          linkedProfileId: data.linkedProfileId,
        });
      }

      // Refresh the outlets list
      await fetchOutlets();
      setShowForm(false);
      setSelectedOutlet(null);
    } catch (err: any) {
      console.error("Failed to save outlet:", err);
      setError("Failed to save outlet. Please try again.");
    } finally {
      setLoading(false);
      setShowForm(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedOutlet(null);
  };

  return (
    <>
      <Styled.StyledContainer>
        <Styled.ContentWrapper $showForm={showForm}>
          <Styled.ListSection $showForm={showForm}>
            <Styled.Header>
              <Styled.HeaderLeftSection>
                Configure Library Outlets
              </Styled.HeaderLeftSection>
              <Styled.HeaderRightSection>
                <Styled.DashboardSearchWrapper>
                  <Styled.SearchBarWrapper>
                    <Styled.SearchIcon src={SearchIcon} alt="Search" />
                    <Styled.SearchInput
                      name="search"
                      type="text"
                      placeholder="Search by name"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </Styled.SearchBarWrapper>
                </Styled.DashboardSearchWrapper>
                <BaseButton onClick={handleCreateOutlet}>
                  Create Outlet
                </BaseButton>
              </Styled.HeaderRightSection>
            </Styled.Header>

            <Styled.Description>
              Configure and manage your library outlets. Link them to different
              content sections and control their availability.
            </Styled.Description>

            {error && (
              <Styled.ErrorMessage>{error}</Styled.ErrorMessage>
            )}

            <Styled.OutletsListContainer>
              {loading && outlets.length === 0 ? (
                <Styled.Description>Loading outlets...</Styled.Description>
              ) : filteredOutlets.length === 0 ? (
                <Styled.Description>
                  {searchQuery
                    ? "No outlets found matching your search."
                    : "No outlets yet. Create your first outlet to get started."}
                </Styled.Description>
              ) : (
                filteredOutlets.map((outlet) => (
                  <OutletCard
                    key={outlet.outletId}
                    title={outlet.outletName}
                    subtitle={outlet.outletUrl}
                    isLinked={!!outlet.linkedProfileId}
                    isEnabled={!outlet.deleted}
                    onEdit={() => handleEditOutlet(outlet)}
                    onToggleEnabled={(enabled) =>
                      handleToggleEnabled(outlet.outletId, enabled)
                    }
                    onGoToProfile={outlet.linkedProfileId ? () => navigate(`/manage-libraries/manage-profiles/${outlet.linkedProfileId}/versions`) : undefined}
                    onEmbedCode={() => { setEmbedCodeOutlet(outlet); setShowEmbedCodeModal(true) }}
                  />
                ))
              )}
            </Styled.OutletsListContainer>
          </Styled.ListSection>

          <Styled.FormSection $showForm={showForm}>
            <Styled.Header>
              <Styled.HeaderLeftSection>
                {selectedOutlet ? "Edit Outlet" : "Create New Outlet"}
              </Styled.HeaderLeftSection>
              <Styled.HeaderRightSection>
                <Styled.CustomButtonGray onClick={handleCancelForm}>
                  Close
                </Styled.CustomButtonGray>
              </Styled.HeaderRightSection>
            </Styled.Header>

            <Styled.Description>
              {selectedOutlet
                ? "Update the outlet configuration below."
                : "Configure a new library outlet to link content to external platforms."}
            </Styled.Description>

            {showForm && (
              <OutletForm
                outlet={selectedOutlet}
                onSave={handleSaveOutlet}
                onCancel={handleCancelForm}
                isEdit={!!selectedOutlet}
                profiles={profiles}
              />
            )}
          </Styled.FormSection>
        </Styled.ContentWrapper>

        {
          showEmbedCodeModal && <EmbedCodeModal
            outletId={embedCodeOutlet?.outletId || "N/A"}
            profileId={embedCodeOutlet?.linkedProfileId || "Unlinked"}
            outletName={embedCodeOutlet?.outletName || "N/A"}
            onClose={() => setShowEmbedCodeModal(false)}
          />
        }
      </Styled.StyledContainer>
    </>
  );
};
